import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Pencil, Plus, Search, Trash2, Upload } from "lucide-react";
import { apiFetch, errorMessage, getMatches } from "@/lib/api";
import { unscopedKey, useScopedKey } from "@/lib/queryClient";
import { useAuthStore } from "@/stores/auth";
import { useScopeStore } from "@/stores/scope";
import { Button } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import { TkticSelect } from "@/components/ui/TkticSelect";
import {
  DataTable,
  DataTableBody,
  DataTableCell,
  DataTableHead,
  DataTableHeadCell,
  DataTablePagination,
  DataTableRow,
  DataTableShell,
} from "@/components/ui/DataTable";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { MatchEditModal } from "@/features/matches/components/MatchEditModal";
import type { Match, Season } from "@/lib/types";
import { PageHeader } from "./components/PageHeader";
import { CoachMatchUploadModal } from "./components/CoachMatchUploadModal";
import { useToast } from "@/components/ui/toast";
import { knownTournamentNames } from "@/features/matches/matchCompetition";

/**
 * Coach > Matches — list the active team's matches with create/upload
 * affordances (M12b).
 *
 * Reads `GET /api/matches?team_id=…` scoped by team ONLY — `season_id`
 * is deliberately suppressed (`seasonId: null`) so the list shows every
 * match for the team across all seasons. Seasons are globally shared;
 * the season name is just an inline detail on each row, not a filter.
 * Injecting `season_id` would narrow `/api/matches` to one season and
 * hide the team's other-season matches (which still show on the
 * unscoped public site).
 * Coaches get the `match:write` capability for teams they're a member of
 * (granted in tenancy.py M12b); the tenancy guard on POST + upload-session
 * endpoints rejects cross-team writes, so coaches can ONLY create + upload
 * matches for the team currently in scope.
 *
 * Two CTAs on every row:
 *   - "Edit" → reopens the same `MatchEditModal` admin uses.
 *   - "Upload video" → opens an upload session modal that drives the
 *     existing chunked-upload API for the chosen slot.
 *
 * The page-level "Add match" button lives in the PageHeader actions
 * slot. New-match flow: open `MatchEditModal` with `match=null`, then
 * on save offer to go straight into upload (the saved match is passed
 * via the `onSaved` callback).
 */
const EMPTY_SEASONS: Season[] = [];
const PAGE_SIZE_OPTIONS = [
  { value: "10", label: "10 / page" },
  { value: "25", label: "25 / page" },
  { value: "50", label: "50 / page" },
];

export function CoachMatchesPage() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const teamId = useScopeStore((s) => s.teamId);
  const teamName = useScopeStore((s) => s.active?.team.name ?? "");
  const authMe = useAuthStore((s) => s.me);
  const seasons = authMe?.seasons ?? EMPTY_SEASONS;
  const canDeleteMatches =
    !!authMe?.user?.is_global_admin || !!authMe?.user?.roles?.includes("admin");
  const matchesQuery = useQuery({
    queryKey: useScopedKey("coach-matches"),
    // `getMatches()` scopes by team only (suppresses `season_id`) so the
    // list shows the team's matches across every season — see its
    // docstring + the route's tktic#8 behavior.
    queryFn: getMatches,
    enabled: !!teamId,
  });

  const [editTarget, setEditTarget] = useState<Match | "new" | null>(null);
  const [uploadTarget, setUploadTarget] = useState<Match | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Match | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<
    "all" | "none" | "ready" | "transcoding" | "error" | "partial"
  >("all");
  const [formatFilter, setFormatFilter] = useState<
    "all" | "full" | "two_halves"
  >("all");
  const [seasonFilter, setSeasonFilter] = useState("all");
  const [sort, setSort] = useState<"date_desc" | "date_asc" | "updated_desc">(
    "date_desc",
  );
  const [pageSize, setPageSize] = useState("10");
  const [page, setPage] = useState(1);
  // Track the most-recently-created match so we can offer to advance
  // straight into the upload flow once the modal closes (M11+ legacy
  // pattern — the coach's expected next step after "create match" is
  // "upload video").
  const pendingUpload = useRef<Match | null>(null);

  const matches = matchesQuery.data ?? [];
  const seasonNameById = useMemo(() => {
    return new Map(seasons.map((s) => [s.id, s.name]));
  }, [seasons]);
  const seasonOptions = useMemo(
    () => [
      { value: "all", label: "All seasons" },
      ...seasons.map((s) => ({
        value: s.id,
        label: s.is_default ? `${s.name} (default)` : s.name,
      })),
    ],
    [seasons],
  );
  const visibleMatches = useMemo(() => {
    const term = search.trim().toLowerCase();
    const filtered = matches.filter((match) => {
      const seasonName = match.season_id
        ? (seasonNameById.get(match.season_id) ?? "")
        : "";
      if (term) {
        const haystack = [
          match.home_team,
          match.away_team,
          match.location,
          match.date,
          match.time,
          match.id,
          match.slug,
          seasonName,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        if (!haystack.includes(term)) return false;
      }
      if (statusFilter !== "all" && deriveAggregateStatus(match) !== statusFilter) {
        return false;
      }
      if (formatFilter !== "all" && deriveFormatGroup(match) !== formatFilter) {
        return false;
      }
      if (seasonFilter !== "all" && match.season_id !== seasonFilter) {
        return false;
      }
      return true;
    });

    return [...filtered].sort((a, b) => {
      if (sort === "updated_desc") {
        return (b.updated_at ?? "").localeCompare(a.updated_at ?? "");
      }
      const da = a.date || "";
      const db = b.date || "";
      if (da !== db) {
        return sort === "date_asc" ? da.localeCompare(db) : db.localeCompare(da);
      }
      return (b.time || "").localeCompare(a.time || "");
    });
  }, [
    formatFilter,
    matches,
    search,
    seasonFilter,
    seasonNameById,
    sort,
    statusFilter,
  ]);
  useEffect(() => {
    setPage(1);
  }, [search, statusFilter, formatFilter, seasonFilter, sort, pageSize]);

  const pageSizeNumber = Number(pageSize);
  const totalPages = Math.max(1, Math.ceil(visibleMatches.length / pageSizeNumber));
  const safePage = Math.min(page, totalPages);
  const pageRows = useMemo(() => {
    const start = (safePage - 1) * pageSizeNumber;
    return visibleMatches.slice(start, start + pageSizeNumber);
  }, [visibleMatches, pageSizeNumber, safePage]);
  const firstVisible =
    visibleMatches.length === 0 ? 0 : (safePage - 1) * pageSizeNumber + 1;
  const lastVisible = Math.min(visibleMatches.length, safePage * pageSizeNumber);
  const readyCount = useMemo(
    () => matches.filter((m) => deriveAggregateStatus(m) === "ready").length,
    [matches],
  );
  const needsVideoCount = useMemo(
    () => matches.filter((m) => deriveAggregateStatus(m) === "none").length,
    [matches],
  );
  const closeDelete = () => {
    if (deleting) return;
    setDeleteTarget(null);
    setDeleteError(null);
  };
  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await apiFetch<{ ok: true }>(
        `/api/matches/${encodeURIComponent(deleteTarget.id)}`,
        {
          method: "DELETE",
          scope: false,
        },
      );
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: unscopedKey("public-matches") }),
        queryClient.invalidateQueries({ queryKey: ["coach-matches"] }),
        queryClient.invalidateQueries({ queryKey: ["admin-matches"] }),
        queryClient.invalidateQueries({ queryKey: ["admin-matches-scoped"] }),
      ]);
      toast.success("Match deleted");
      setDeleteTarget(null);
    } catch (err) {
      const message = errorMessage(err);
      setDeleteError(message);
      toast.error("Couldn't delete match", { description: message });
    } finally {
      setDeleting(false);
    }
  };

  if (!teamId) {
    return (
      <div className="flex flex-col gap-5" data-testid="coach-matches-page">
        <p
          className="text-ink-muted text-sm font-mono px-3 py-6"
          data-testid="coach-matches-no-team"
        >
          No team selected.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5" data-testid="coach-matches-page">
      {deleteTarget ? (
        <ConfirmDialog
          title="Delete match?"
          message={
            <div className="flex flex-col gap-2">
              <p>
                <strong>
                  {deleteTarget.home_team || "—"} vs{" "}
                  {deleteTarget.away_team || "—"}
                </strong>{" "}
                ({deleteTarget.date || "no date"}) will be permanently removed
                along with uploaded video and linked match evidence.
              </p>
              {deleteError ? (
                <p
                  data-testid="coach-matches-delete-error"
                  className="font-mono text-xs text-destructive"
                >
                  {deleteError}
                </p>
              ) : null}
            </div>
          }
          confirmLabel={deleting ? "Deleting..." : "Delete match"}
          danger
          onConfirm={() => void confirmDelete()}
          onClose={closeDelete}
          data-testid="coach-matches-delete-confirm"
        />
      ) : null}
      <PageHeader
        title="Matches"
        description={
          <>
            {teamName ? `${teamName} · ` : ""}Create matches and upload video
            for your team.
          </>
        }
        actions={
          <Button
            type="button"
            onClick={() => setEditTarget("new")}
            data-testid="coach-matches-add"
            aria-label="Add match"
            title="Add match"
            size="sm"
            className="h-8 w-8 px-0"
          >
            <Plus size={16} strokeWidth={2} aria-hidden="true" />
          </Button>
        }
      />

      {matchesQuery.isLoading ? (
        <p
          data-testid="coach-matches-loading"
          className="font-mono text-sm text-ink-muted"
        >
          Loading matches…
        </p>
      ) : matchesQuery.isError ? (
        <p
          data-testid="coach-matches-error"
          className="font-mono text-sm text-destructive"
        >
          Couldn&rsquo;t load matches.
        </p>
      ) : matches.length === 0 ? (
        <p
          data-testid="coach-matches-empty"
          className="font-mono text-sm text-ink-muted"
        >
          No matches yet. Use{" "}
          <button
            type="button"
            onClick={() => setEditTarget("new")}
            className="text-accent underline underline-offset-2 hover:no-underline"
          >
            Add Match
          </button>{" "}
          to create the first one.
        </p>
      ) : (
        <>
          <div
            className="grid grid-cols-3 gap-2"
            aria-label="Match summary"
            data-testid="coach-matches-summary"
          >
            <MatchStat label="Total" value={matches.length} />
            <MatchStat label="Ready" value={readyCount} />
            <MatchStat label="Needs video" value={needsVideoCount} />
          </div>

          <div className="flex flex-wrap items-center gap-2 rounded border border-rule bg-surface p-2 shadow-sm">
            <label className="relative min-w-[14rem] flex-1">
              <Search
                size={15}
                strokeWidth={2}
                aria-hidden="true"
                className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-muted"
              />
              <span className="sr-only">Search matches</span>
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search teams, location, date..."
                data-testid="coach-matches-search"
                className="w-full rounded border border-rule bg-surface py-1.5 pl-8 pr-2 text-xs text-ink focus:outline-none focus:ring-2 focus:ring-accent"
              />
            </label>
            <div className="w-full sm:w-36">
              <TkticSelect
                value={statusFilter}
                onChange={(next) => setStatusFilter(next as typeof statusFilter)}
                data-testid="coach-matches-status-filter"
                size="sm"
                ariaLabel="Filter by video status"
                options={[
                  { value: "all", label: "All status" },
                  { value: "ready", label: "Ready" },
                  { value: "partial", label: "Partial" },
                  { value: "transcoding", label: "Transcoding" },
                  { value: "error", label: "Error" },
                  { value: "none", label: "Needs video" },
                ]}
              />
            </div>
            <div className="w-full sm:w-32">
              <TkticSelect
                value={formatFilter}
                onChange={(next) => setFormatFilter(next as typeof formatFilter)}
                data-testid="coach-matches-format-filter"
                size="sm"
                ariaLabel="Filter by video layout"
                options={[
                  { value: "all", label: "Any format" },
                  { value: "full", label: "Full" },
                  { value: "two_halves", label: "Halves" },
                ]}
              />
            </div>
            <div className="w-full sm:w-44">
              <TkticSelect
                value={seasonFilter}
                onChange={setSeasonFilter}
                data-testid="coach-matches-season-filter"
                size="sm"
                ariaLabel="Filter by season"
                options={seasonOptions}
              />
            </div>
            <div className="w-full sm:w-36">
              <TkticSelect
                value={sort}
                onChange={(next) => setSort(next as typeof sort)}
                data-testid="coach-matches-sort"
                size="sm"
                ariaLabel="Sort matches"
                options={[
                  { value: "date_desc", label: "Newest first" },
                  { value: "date_asc", label: "Oldest first" },
                  { value: "updated_desc", label: "Recently edited" },
                ]}
              />
            </div>
            <div className="w-full sm:w-32">
              <TkticSelect
                value={pageSize}
                onChange={setPageSize}
                data-testid="coach-matches-page-size"
                size="sm"
                ariaLabel="Rows per page"
                options={PAGE_SIZE_OPTIONS}
              />
            </div>
          </div>

          {visibleMatches.length === 0 ? (
            <div
              data-testid="coach-matches-no-results"
              className="rounded border border-rule bg-surface px-3 py-6 text-center text-sm text-ink-muted"
            >
              No matches match those filters.
              <button
                type="button"
                onClick={() => {
                  setSearch("");
                  setStatusFilter("all");
                  setFormatFilter("all");
                  setSeasonFilter("all");
                  setSort("date_desc");
                }}
                className="ml-2 text-accent hover:underline"
              >
                Clear filters
              </button>
            </div>
          ) : (
            <DataTableShell
              data-testid="coach-matches-list"
              className="p-2 md:p-0"
            >
              <DataTable responsive className="text-sm">
                <DataTableHead responsive>
                  <tr>
                    <DataTableHeadCell>
                      Match
                    </DataTableHeadCell>
                    <DataTableHeadCell>
                      Date
                    </DataTableHeadCell>
                    <DataTableHeadCell>
                      Season
                    </DataTableHeadCell>
                    <DataTableHeadCell>
                      Format
                    </DataTableHeadCell>
                    <DataTableHeadCell>
                      Video
                    </DataTableHeadCell>
                    <DataTableHeadCell align="right">
                      Actions
                    </DataTableHeadCell>
                  </tr>
                </DataTableHead>
                <DataTableBody responsive>
                  {pageRows.map((match) => (
                    <CoachMatchRow
                      key={match.id}
                      match={match}
                      seasonName={
                        match.season_id
                          ? (seasonNameById.get(match.season_id) ?? null)
                          : null
                      }
                      onEdit={() => setEditTarget(match)}
                      onUpload={() => setUploadTarget(match)}
                      onDelete={
                        canDeleteMatches ? () => setDeleteTarget(match) : undefined
                      }
                    />
                  ))}
                </DataTableBody>
              </DataTable>
            </DataTableShell>
          )}
          {visibleMatches.length > 0 ? (
            <DataTablePagination
              first={firstVisible}
              last={lastVisible}
              total={visibleMatches.length}
              page={safePage}
              totalPages={totalPages}
              onPrevious={() => setPage((p) => Math.max(1, p - 1))}
              onNext={() => setPage((p) => Math.min(totalPages, p + 1))}
              summaryTestId="coach-matches-pagination-summary"
              previousTestId="coach-matches-prev-page"
              indicatorTestId="coach-matches-page-indicator"
              nextTestId="coach-matches-next-page"
            />
          ) : null}
        </>
      )}

      {editTarget ? (
        <MatchEditModal
          match={editTarget === "new" ? null : editTarget}
          knownTournaments={knownTournamentNames(matches)}
          onSaved={(saved) => {
            // On a fresh create we remember the new match so the
            // close handler below can prompt for upload. On edit we
            // don't carry pending state — the user explicitly opens
            // upload from the row.
            if (editTarget === "new") pendingUpload.current = saved;
          }}
          onClose={() => {
            setEditTarget(null);
            const next = pendingUpload.current;
            pendingUpload.current = null;
            if (next) setUploadTarget(next);
          }}
        />
      ) : null}

      {uploadTarget ? (
        <CoachMatchUploadModal
          match={uploadTarget}
          onClose={() => setUploadTarget(null)}
        />
      ) : null}
    </div>
  );
}

function CoachMatchRow({
  match,
  seasonName,
  onEdit,
  onUpload,
  onDelete,
}: {
  match: Match;
  seasonName: string | null;
  onEdit: () => void;
  onUpload: () => void;
  onDelete?: () => void;
}) {
  const slots = match.video_status ?? {};
  const ready = Object.values(slots).filter((s) => s === "ready").length;
  const transcoding = Object.values(slots).filter(
    (s) => s === "transcoding",
  ).length;
  const errored = Object.values(slots).filter((s) => s === "error").length;
  const aggregateStatus = deriveAggregateStatus(match);
  return (
    <DataTableRow
      data-testid="coach-match-row"
      data-match-id={match.id}
      className="mb-2 block rounded border border-rule bg-surface p-3 shadow-sm last:mb-0 md:table-row md:rounded-none md:border-0 md:border-b md:p-0 md:shadow-none md:last:border-b-0 md:hover:bg-field"
    >
      <DataTableCell className="block pb-3 align-middle md:table-cell md:px-3 md:py-2.5">
        <div className="min-w-0">
          <p className="truncate font-display text-sm text-ink md:max-w-[18rem]">
            {match.home_team || "—"} vs {match.away_team || "—"}
          </p>
          <p className="truncate font-mono text-[0.65rem] uppercase tracking-mono text-ink-muted md:max-w-[18rem]">
            {match.location || "No location"}
          </p>
        </div>
      </DataTableCell>
      <DataTableCell className="flex items-start justify-between gap-3 border-t border-rule py-2 align-middle font-mono text-xs text-ink md:table-cell md:whitespace-nowrap md:border-t-0 md:px-3 md:py-2.5">
        <span className="font-display text-[0.65rem] uppercase tracking-wider text-ink-muted md:hidden">
          Date
        </span>
        <span className="text-right md:text-left">
          {match.date || "No date"}
          {match.time ? (
            <span className="block text-[0.65rem] uppercase tracking-mono text-ink-muted">
              {match.time}
            </span>
          ) : null}
        </span>
      </DataTableCell>
      <DataTableCell
        data-testid="coach-match-row-season"
        className="flex items-center justify-between gap-3 border-t border-rule py-2 align-middle text-right text-xs text-ink-muted md:table-cell md:whitespace-nowrap md:border-t-0 md:px-3 md:py-2.5 md:text-left"
      >
        <span className="font-display text-[0.65rem] uppercase tracking-wider text-ink-muted md:hidden">
          Season
        </span>
        <span>{seasonName ?? "—"}</span>
      </DataTableCell>
      <DataTableCell className="flex items-center justify-between gap-3 border-t border-rule py-2 align-middle md:table-cell md:border-t-0 md:px-3 md:py-2.5">
        <span className="font-display text-[0.65rem] uppercase tracking-wider text-ink-muted md:hidden">
          Format
        </span>
        <FormatBadge format={match.format} />
      </DataTableCell>
      <DataTableCell className="flex items-start justify-between gap-3 border-t border-rule py-2 align-middle md:table-cell md:border-t-0 md:px-3 md:py-2.5">
        <span className="font-display text-[0.65rem] uppercase tracking-wider text-ink-muted md:hidden">
          Video
        </span>
        <div className="flex flex-wrap items-center justify-end gap-1.5 md:justify-start">
          <StatusBadge status={aggregateStatus} />
          {ready > 0 ? (
            <Badge variant="good" data-testid="coach-match-row-ready">
              {ready} ready
            </Badge>
          ) : null}
          {transcoding > 0 ? (
            <Badge variant="flag" data-testid="coach-match-row-transcoding">
              {transcoding} transcode
            </Badge>
          ) : null}
          {errored > 0 ? (
            <Badge
              variant="outline"
              data-testid="coach-match-row-error"
              className="border-destructive text-destructive"
            >
              {errored} error
            </Badge>
          ) : null}
        </div>
      </DataTableCell>
      <DataTableCell className="border-t border-rule pt-3 align-middle md:border-t-0 md:px-3 md:py-2.5">
        <div className="flex items-center justify-end gap-1">
          <button
            type="button"
            onClick={onEdit}
            data-testid="coach-match-row-edit"
            aria-label={`Edit ${match.home_team || "match"} vs ${match.away_team || "opponent"}`}
            title="Edit match"
            className="inline-flex h-8 w-8 items-center justify-center rounded text-ink-muted transition-colors hover:bg-field hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          >
            <Pencil size={16} strokeWidth={2} aria-hidden="true" />
          </button>
          <button
            type="button"
            onClick={onUpload}
            data-testid="coach-match-row-upload"
            aria-label={`Upload video for ${match.home_team || "match"} vs ${match.away_team || "opponent"}`}
            title="Upload video"
            className="inline-flex h-8 w-8 items-center justify-center rounded bg-accent text-accent-ink transition-colors hover:bg-accent-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-canvas"
          >
            <Upload size={16} strokeWidth={2} aria-hidden="true" />
          </button>
          {onDelete ? (
            <button
              type="button"
              onClick={onDelete}
              data-testid="coach-match-row-delete"
              aria-label={`Delete ${match.home_team || "match"} vs ${match.away_team || "opponent"}`}
              title="Delete match"
              className="inline-flex h-8 w-8 items-center justify-center rounded text-destructive transition-colors hover:bg-destructive/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
            >
              <Trash2 size={16} strokeWidth={2} aria-hidden="true" />
            </button>
          ) : null}
        </div>
      </DataTableCell>
    </DataTableRow>
  );
}

function MatchStat({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded border border-rule bg-surface px-3 py-2 shadow-sm">
      <p className="font-mono text-2xl leading-none text-ink">{value}</p>
      <p className="mt-1 text-[0.65rem] font-display uppercase tracking-wider text-ink-muted">
        {label}
      </p>
    </div>
  );
}

function FormatBadge({ format }: { format: string }) {
  const label =
    format === "two_halves" ? "Halves" : format === "full" ? "Full" : format;
  return (
    <span className="inline-flex rounded border border-rule bg-elevated px-2 py-1 font-mono text-[0.65rem] uppercase tracking-mono text-ink-muted">
      {label}
    </span>
  );
}

function StatusBadge({
  status,
}: {
  status: "none" | "ready" | "transcoding" | "error" | "partial";
}) {
  if (status === "ready") {
    return <Badge variant="good">Ready</Badge>;
  }
  if (status === "transcoding") {
    return <Badge variant="flag">Transcoding</Badge>;
  }
  if (status === "error") {
    return (
      <Badge variant="outline" className="border-destructive text-destructive">
        Error
      </Badge>
    );
  }
  if (status === "partial") {
    return <Badge variant="accent">Partial</Badge>;
  }
  return <Badge variant="muted">Needs video</Badge>;
}

function deriveAggregateStatus(
  match: Match,
): "none" | "ready" | "transcoding" | "error" | "partial" {
  const statuses = Object.values(match.video_status ?? {});
  if (statuses.length === 0) return "none";
  if (statuses.every((s) => s === "ready")) return "ready";
  if (statuses.some((s) => s === "transcoding")) return "transcoding";
  if (statuses.some((s) => s === "error")) return "error";
  return "partial";
}

function deriveFormatGroup(match: Match): "full" | "two_halves" | "other" {
  if (match.format === "full") return "full";
  if (match.format === "two_halves") return "two_halves";
  return "other";
}
